'use client';

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  Typography,
  Input,
  Checkbox,
  CustomHeroButton,
  AlertBlock,
  CardWrapper,
  ResponsiveModal,
} from '../../../../components';
import { RadioGroup, Slider, useRadio, VisuallyHidden, cn } from '@heroui/react';
import { ProposalData } from '../types';
import { getTierRangeLabel, transformProposalOfferToProposalData } from '../utils';
import clientCplService from '../../../../api/client/clientCplService';
import { clientAuthService } from '../../../../api';
import { useAuth } from '../../../../contexts/AuthContext';
import { useAccount } from '../../../../contexts/AccountContext';
import type { Account } from '../../../../types/account';
import AccountSelect from './components/AccountSelect';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  useDisclosure,
} from '@heroui/react';
import { IcModalSuccess } from '../../../../components/icons';
import { useTranslations } from '../../../../contexts/translation.context';
import { usePageBreadcrumbs } from '../../../../hooks/usePageBreadcrumbs';
import { parseApiError } from '../../../../utils';
/** Длительность одной подписки в днях. */
const SUBSCRIPTION_PERIOD_DAYS = 30;

const CreateOrder: React.FC = () => {
  const { t } = useTranslations();
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [proposal, setProposal] = useState<ProposalData | null>(null);
  const [clientLvl, setClientLvl] = useState<any>(null);
  const [currentCommission, setCurrentCommission] = useState<number>(17);

  // Устанавливаем хлебные крошки
  // usePageBreadcrumbs([
  //   { label: "Главная", isActive: false, href: "/client/dashboard" },
  //   { label: "Предложения", isActive: false, href: "/client/subscribers/proposals" },
  //   { label: "Заказ", isActive: true }
  // ]);
  const breadcrumbs = useMemo(
    () => [
      { label: t('proposals.home'), href: '/' },
      { label: t('proposals.proposals'), href: '/client/subscribers/proposals' },
      { label: t('proposals.createOrder.title'), isActive: true },
    ],
    [t]
  );
  usePageBreadcrumbs(breadcrumbs);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { accounts } = useAccount();
  // Form states
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
  const [subscribers, setSubscribers] = useState<string>('30');
  const [campaignType, setCampaignType] = useState<string>('');
  const [contentLink, setContentLink] = useState<string>('');
  const [contentTypes, setContentTypes] = useState<string[]>([]);
  const [autoRenewal, setAutoRenewal] = useState<boolean>(false);
  const [hideStats, setHideStats] = useState<boolean>(false);

  const selectedAccountObjects = useMemo(
    () =>
      selectedAccounts
        .map((accountId) => accounts.find((acc) => acc.id === accountId))
        .filter((acc): acc is Account => Boolean(acc)),
    [accounts, selectedAccounts]
  );

  // Work media states for user-selectable content types
  const [workMediaStates, setWorkMediaStates] = useState<{ [key: string]: boolean }>({
    lite_nude: false,
    full_nude: false,
  });

  // Campaign types from API
  const [campaignTypes, setCampaignTypes] = useState<string[]>([]);

  // Debug: отслеживаем изменения campaignTypes
  useEffect(() => {
    console.log('campaignTypes state changed:', campaignTypes);
  }, [campaignTypes]);

  // Error states
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [showError, setShowError] = useState<boolean>(false);

  // Proposal data states
  const [proposalData, setProposalData] = useState<any>(null);
  const [subsMin, setSubsMin] = useState<number>(100);
  const [subsMax, setSubsMax] = useState<number>(1100);
  const [subsPerDay, setSubsPerDay] = useState<number>(10);
  const [pricePerSub, setPricePerSub] = useState<number>(1.5);
  const [orderIds, setOrderIds] = useState<string[]>([]);
  // У подписки цена фиксирована и не зависит от количества фанов.
  const isSubscription = proposal?.offerType === 'subscription';
  // Load proposal data by ID
  useEffect(() => {
    const loadProposal = async () => {
      if (id) {
        try {
          setIsLoading(true);
          const response = await clientCplService.getProposalById(id);
          if (response && response.data) {
            const proposalOffer = response.data;
            const transformedProposal = transformProposalOfferToProposalData(proposalOffer, t);
            setProposal(transformedProposal);
            setProposalData(proposalOffer);

            const isSubscriptionOffer = proposalOffer.type === 'subscription';

            // Устанавливаем данные для слайдера
            setSubsMin(proposalOffer.subs_min);
            setSubsMax(proposalOffer.subs_max);
            setSubsPerDay(proposalOffer.subs_pre_day);

            // У подписки выбирать количество нельзя — берём ориентировочный
            // объём фанов в месяц из оффера. Для CPL — нижняя граница диапазона.
            setSubscribers(
              isSubscriptionOffer
                ? proposalOffer.subs_pre_day?.toString() || '0'
                : proposalOffer.subs_min?.toString() || '100'
            );

            // Рассчитываем цену за подписчика на основе tiers (без комиссии)
            if (proposalOffer.subs && proposalOffer.subs.length > 0) {
              const firstTier = proposalOffer.subs[0];
              setPricePerSub(parseFloat(firstTier.price));
            }
            if (
              proposalOffer.campaign_types &&
              Array.isArray(proposalOffer.campaign_types) &&
              proposalOffer.campaign_types.length > 0
            ) {
              setCampaignTypes(proposalOffer.campaign_types);
              // Устанавливаем первый тип как выбранный по умолчанию
              setCampaignType(proposalOffer.campaign_types[0]);
            } else {
              console.log('No campaign types found in API response');
              console.log('Available fields:', Object.keys(proposalOffer));
            }
          }
        } catch (error) {
          console.error('Error loading proposal:', error);
        } finally {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    };

    loadProposal();
  }, [id]);

  // Загружаем данные уровня клиента для комиссии
  useEffect(() => {
    const loadClientLvl = async () => {
      try {
        const response = await clientAuthService.getClientLvl();
        if (response?.data) {
          setClientLvl(response.data);
        }
      } catch (error) {
        console.error('Error loading client level:', error);
      }
    };

    loadClientLvl();
  }, []);

  // Вычисляем комиссию на основе уровня пользователя
  useEffect(() => {
    if (user?.lvl && clientLvl) {
      const commission = clientLvl[user.lvl]?.commition || 17;
      setCurrentCommission(commission);
    }
  }, [user, clientLvl]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleCloseError = () => {
    setShowError(false);
    setErrorMessage('');
  };

  const buildWorkMediaObject = (selectedAccountId: string) => {
    // Find the selected account from the accounts list
    const selectedAccount = accounts.find((acc) => acc.id === selectedAccountId);

    if (!selectedAccount) {
      return null;
    }

    // Always set nonude to true
    const workMedia: any = {
      nonude: true,
    };

    // Check if account has work_content and set disabled fields accordingly
    if (selectedAccount.work_content) {
      workMedia.tt = selectedAccount.work_content.tt ? true : false;
      workMedia.lingerie = selectedAccount.work_content.lingerie ? true : false;
    } else {
      // If no work_content, set disabled fields to false
      workMedia.tt = false;
      workMedia.lingerie = false;
    }

    // Use user-selected states for selectable content types
    workMedia.lite_nude = workMediaStates.lite_nude;
    workMedia.full_nude = workMediaStates.full_nude;

    return workMedia;
  };

  const getWorkMediaStatus = (fieldName: string) => {
    if (selectedAccountObjects.length === 0) return false;

    // nonude is always true when we have at least one account
    if (fieldName === 'nonude') return true;

    // For disabled fields (tt, lingerie), check availability across all selected accounts
    if (fieldName === 'tt' || fieldName === 'lingerie') {
      return selectedAccountObjects.every((account) => {
        if (!account.work_content) return false;
        return !!account.work_content[fieldName];
      });
    }

    // For user-selectable fields (lite_nude, full_nude), use shared state
    if (fieldName === 'lite_nude' || fieldName === 'full_nude') {
      return workMediaStates[fieldName] || false;
    }

    return false;
  };

  const handleWorkMediaChange = (fieldName: string, checked: boolean) => {
    // Only allow changes for user-selectable fields
    if (fieldName === 'lite_nude' || fieldName === 'full_nude') {
      setWorkMediaStates((prev) => ({
        ...prev,
        [fieldName]: checked,
      }));
    }
  };

  const handleSubmit = async () => {
    if (selectedAccountObjects.length === 0 || !proposalData) {
      setErrorMessage(t('proposals.createOrder.errors.noAccountOrProposal'));
      setShowError(true);
      return;
    }

    try {
      const createdOrderIds: string[] = [];

      for (const accountId of selectedAccountObjects.map((acc) => acc.id)) {
        const workMedia = buildWorkMediaObject(accountId);
        if (!workMedia) {
          console.warn('Skipped account without work media data', accountId);
          continue;
        }

        // Маппинг типа кампании: с бэка приходит "trial", отправлять нужно "free trial"
        const mappedCampaignType = campaignType === 'trial' ? 'free trial' : campaignType;

        const orderData = {
          subs: {
            count: parseInt(subscribers),
          },
          model: {
            id: accountId,
            type: mappedCampaignType
          },
          type: mappedCampaignType,
          offer: proposalData.id,
          access_work_media: workMedia,
        };

        console.log('Order data being sent:', orderData);

        const response = await clientCplService.createOrder(orderData);
        createdOrderIds.push(response?.data?.id || accountId);
      }

      if (createdOrderIds.length === 0) {
        throw new Error(t('proposals.createOrder.errors.createOrderError'));
      }

      setOrderIds(createdOrderIds);
      setShowError(false);
      setErrorMessage('');
      onOpen();
    } catch (error: any) {
      console.error('Error creating order:', error);
      let errorMsg = t('proposals.createOrder.errors.createOrderError');
      if (error?.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      } else if (error?.message) {
        errorMsg = error.message;
      }
      const errmsg: any =
        parseApiError(error) || t('proposals.createOrder.errors.createOrderError');
      setErrorMessage(errmsg);
      setShowError(true);
    }
  };

  const handleContentTypeChange = (type: string, checked: boolean) => {
    if (checked) {
      setContentTypes((prev) => [...prev, type]);
    } else {
      setContentTypes((prev) => prev.filter((t) => t !== type));
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary mb-4"></div>
          <Typography variant="h6">{t('proposals.createOrder.loading')}</Typography>
        </div>
      </div>
    );
  }

  if (!proposal) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Typography variant="h6" className="mb-4">
            {t('proposals.createOrder.proposalNotFound')}
          </Typography>
          <CustomHeroButton onPress={handleBack} title={t('proposals.createOrder.goBack')} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header with buttons */}
      <div className="px-4 pt-6 md:px-6 md:pt-4 ">
        <div className="flex items-center justify-between">
          <Typography variant="h4" className="font-semibold text-auth-ink">
            {t('proposals.createOrder.title')}
          </Typography>
        </div>
      </div>
      <div className="md:flex-1 px-4 py-6 md:p-6 md:overflow-y-auto">
        <div className="flex flex-col gap-4">
          <CardWrapper className="flex flex-col gap-4">
            <div className="max-w-[37.5rem]">
              <label className="block text-sm font-medium text-auth-ink mb-2">
                {t('proposals.createOrder.account')}
              </label>
              <AccountSelect
                accounts={accounts}
                selectedAccounts={selectedAccounts}
                onSelectionChange={setSelectedAccounts}
                placeholder={t('proposals.createOrder.selectAccount')}
              />
            </div>
            <div className="max-w-[37.5rem]">
              <label className="block text-sm font-medium text-auth-ink mb-2">
                {t('proposals.createOrder.subscribers')}
              </label>
              <Input
                type="number"
                placeholder="30"
                value={subscribers}
                // У подписки количество задаёт маркетолог (фанов/мес), менять его нельзя.
                isReadOnly={isSubscription}
                isDisabled={isSubscription}
                onChange={(value) => {
                  if (isSubscription) return;
                  setSubscribers(value);

                  const numericValue = parseInt(value || '0', 10);
                  if (Number.isNaN(numericValue)) {
                    return;
                  }

                  // Перераховуємо ціну по тирах так само, як у слайдері
                  if (proposalData?.subs) {
                    const sortedTiers = [...proposalData.subs].sort((a, b) => b.ge - a.ge);
                    const selectedTier =
                      sortedTiers.find((tier) => numericValue >= tier.ge) ||
                      sortedTiers[sortedTiers.length - 1];
                    if (selectedTier) {
                      setPricePerSub(parseFloat(selectedTier.price));
                    }
                  }
                }}
                onBlur={() => {
                  if (isSubscription) return;
                  const numericValue = parseInt(subscribers, 10);

                  if (Number.isNaN(numericValue)) {
                    setSubscribers(String(subsMin));
                    return;
                  }

                  const clampedValue = Math.min(Math.max(numericValue, subsMin), subsMax);

                  setSubscribers(String(clampedValue));
                }}
                variant="bordered"
                className="w-full h-[2.375rem]"
                classNames={{
                  inputWrapper: `border-auth-strength-none border-[1px] max-h-[2.375rem] min-h-[2.375rem] rounded-lg h-10 `,
                }}
              />
            </div>

            <div className="mb-4">
              <div className="space-y-4">
                {/* Price Range Slider — у подписки количество не выбирается */}
                {!isSubscription && (
                  <Slider
                    aria-label="subscribers"
                    className="max-w-[37.5rem]"
                    color="primary"
                    value={parseInt(subscribers)}
                    maxValue={subsMax}
                    minValue={subsMin}
                    showOutline={true}
                    size="sm"
                    step={1}
                    onChange={(value) => {
                      const newValue = Array.isArray(value) ? value[0] : value;
                      setSubscribers(newValue.toString());

                      // Рассчитываем цену на основе tiers
                      // Сортируем по убыванию ge и находим первый tier, где количество >= ge
                      if (proposalData?.subs) {
                        const sortedTiers = [...proposalData.subs].sort((a, b) => b.ge - a.ge);
                        const selectedTier =
                          sortedTiers.find((tier) => newValue >= tier.ge) ||
                          sortedTiers[sortedTiers.length - 1];
                        setPricePerSub(parseFloat(selectedTier.price));
                      }
                    }}
                  />
                )}

                {/* Price tiers row (if offer has tiered pricing) */}
                {!isSubscription && proposal.price?.tiers?.length > 0 && (
                  <div className="max-w-[37.5rem] mb-3">
                    <div className="mb-1 text-xs font-medium text-auth-muted">
                      {t('proposals.createOrder.priceTiersLabel')}
                    </div>
                    <div className="rounded-lg bg-gray-96 text-auth-ink px-4 py-2.5 overflow-x-auto border border-gray-90">
                      <div className="flex flex-wrap md:flex-nowrap items-center gap-3 md:gap-6 min-w-max text-xs">
                        {proposal.price.tiers.map((tier, index) => {
                          const rangeLabel = getTierRangeLabel(proposal.price.tiers, index);
                          return (
                            <div
                              key={`${tier.from}-${tier.to ?? 'plus'}`}
                              className="flex items-center gap-2 whitespace-nowrap"
                            >
                              <span className="text-xs text-auth-muted">{rangeLabel}</span>
                              <span className="text-sm font-semibold text-auth-ink">
                                ${tier.price.toFixed(2)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                <div className="border border-gray-90 max-w-[37.5rem] rounded-3.5 overflow-hidden">
                  <div className="grid grid-cols-1 md:grid-cols-3">
                    <div className="p-4 border-r border-gray-90 last:border-r-0">
                      <div className="text-sm leading-[1.125rem] text-neutral-800 mb-2">
                        {isSubscription
                          ? t('proposals.createOrder.pricePerSubscription')
                          : t('proposals.createOrder.pricePerFan')}
                      </div>
                      <div className="text-2xl font-semibold text-neutral-900">
                        ${pricePerSub.toFixed(2)}
                      </div>
                    </div>
                    <div className="p-4 border-r border-gray-90 last:border-r-0">
                      <div className="text-sm leading-[1.125rem] text-neutral-800 mb-2">
                        {t('proposals.createOrder.amount')}
                      </div>
                      <div className="text-2xl font-semibold text-neutral-900">
                        {/* У подписки платим за саму подписку — без умножения на фанов. */}
                        $
                        {(
                          (isSubscription ? 1 : parseInt(subscribers || '0')) *
                          pricePerSub *
                          (1 + (currentCommission || 0) / 100)
                        ).toFixed(2)}
                      </div>
                      <div className="text-xxs text-auth-muted mt-2">
                        {t('proposals.createOrder.commission', {
                          percent: currentCommission,
                        })}
                      </div>
                    </div>
                    <div className="p-4 border-r border-gray-90 last:border-r-0">
                      <div className="text-sm leading-[1.125rem] text-neutral-800 mb-2">
                        {t('proposals.createOrder.estimatedDays')}
                      </div>
                      <div className="text-2xl font-semibold text-neutral-900">
                        {/* Подписка всегда на месяц; для CPL — подписчики / фанов в день. */}
                        {isSubscription
                          ? SUBSCRIPTION_PERIOD_DAYS
                          : Math.ceil(parseInt(subscribers) / subsPerDay)}
                      </div>
                      <div className="text-xxs text-auth-muted mt-2">
                        (
                        {t('proposals.createOrder.perDay', {
                          count: isSubscription
                            ? Math.round(subsPerDay / SUBSCRIPTION_PERIOD_DAYS)
                            : subsPerDay,
                        })}
                        )
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </CardWrapper>
          <AlertBlock
            type="default"
            data={[
              {
                title: t('proposals.createOrder.purchaseTerms.title'),
                severity: 'info',
                description: t('proposals.createOrder.purchaseTerms.description'),
              },
            ]}
          />
          <CardWrapper className="flex flex-col items-start">
            <div className="mb-4">
              <label className="block text-sm font-medium text-auth-ink mb-2">
                {t('proposals.createOrder.campaignType')}
              </label>
              {campaignTypes.length > 0 ? (
                <RadioGroup
                  className="flex"
                  value={campaignType}
                  onValueChange={setCampaignType}
                  classNames={{
                    wrapper: 'flex flex-row',
                  }}
                >
                  {campaignTypes.map((type) => (
                    <CustomRadio key={type} value={type}>
                      {type}
                    </CustomRadio>
                  ))}
                </RadioGroup>
              ) : (
                <div>
                  <Typography variant="body2" className="text-auth-muted">
                    {t('proposals.createOrder.campaignTypesNotLoaded')}
                  </Typography>
                </div>
              )}
            </div>
          </CardWrapper>
          <CardWrapper className="p-4">
            <div>
              <label className="block text-sm font-medium text-auth-ink mb-2">
                {t('proposals.createOrder.contentLink')}
              </label>
              <div className="flex flex-wrap gap-4">
                {[
                  { label: 'No nude/Lifestyle', field: 'nonude', disabled: true },
                  { label: 'TikTok', field: 'tt', disabled: true },
                  { label: 'Lingerie', field: 'lingerie', disabled: true },
                  { label: 'Light Nudes', field: 'lite_nude', disabled: false },
                  { label: 'Full Nude', field: 'full_nude', disabled: false },
                ].map(({ label, field, disabled }) => {
                  const isAvailable = getWorkMediaStatus(field);
                  return (
                    <label
                      key={field}
                      className={`flex items-center gap-2 ${disabled && 'cursor-not-allowed opacity-50'}`}
                    >
                      <Checkbox
                        isSelected={isAvailable}
                        isDisabled={!selectedAccountObjects.length || disabled}
                        onChange={(checked) => handleWorkMediaChange(field, checked)}
                        className="w-5 h-5 rounded-md border-[#e0e0e0]"
                      />
                      <span
                        className={`text-sm ${isAvailable ? 'text-auth-ink' : 'text-auth-muted'}`}
                      >
                        {label}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          </CardWrapper>
          <AlertBlock
            type="default"
            data={[
              {
                title: t('proposals.createOrder.requirements.title'),
                severity: 'info',
                description:
                  proposalData?.requirements || t('proposals.createOrder.requirements.noData'),
              },
            ]}
          />
          {showError && (
            <AlertBlock
              type="rounded"
              data={[
                {
                  title: t('proposals.createOrder.error'),
                  severity: 'error',
                  description: errorMessage,
                },
              ]}
            />
          )}
          <div className="flex pt-6">
            <CustomHeroButton
              variant="primary"
              onPress={handleSubmit}
              className="px-4 md:px-6 py-3 h-10 rounded-xl bg-primary text-white font-semibold"
              title={t('proposals.createOrder.submit')}
            />
          </div>
        </div>
      </div>

      <ResponsiveModal isOpen={isOpen} onClose={onClose}>
        <ModalContent className="md:w-[25rem]">
          <ModalHeader className="flex items-center gap-2">
            <IcModalSuccess />
            <Typography variant="h4">{t('proposals.createOrder.modal.congratulations')}</Typography>
          </ModalHeader>
          <ModalBody className="pt-3 px-4 md:px-6 pb-6">
            <Typography variant="body2">
              {t('proposals.createOrder.modal.orderCreated', { orderId: orderIds.join(', ') })}
            </Typography>
          </ModalBody>
          <ModalFooter>
            <CustomHeroButton
              onPress={() => navigate('/client/subscribers/orders')}
              title={t('proposals.createOrder.modal.continue')}
            />
          </ModalFooter>
        </ModalContent>
      </ResponsiveModal>
    </div>
  );
};

export default CreateOrder;

export const CustomRadio = (props: any) => {
  const {
    Component,
    children,
    getBaseProps,
    getWrapperProps,
    getInputProps,
    getLabelProps,
    getLabelWrapperProps,
    getControlProps,
  } = useRadio(props);

  return (
    <Component
      {...getBaseProps()}
      className={cn(
        'group inline-flex flex items-center hover:opacity-70 active:opacity-50 flex-row-reverse tap-highlight-transparent',
        'max-w-[18.75rem] max-h-[3.25rem] bg-gray-98 border-auth-surface cursor-pointer border-2 border-default rounded-lg gap-0 p-0',
        'data-[selected=true]:border-primary rounded-3 p-4',
        `${children === 'free' ? 'uppercase' : 'capitalize'}`
      )}
    >
      <div {...getLabelWrapperProps()}>
        {children && <span {...getLabelProps()}>{children}</span>}
      </div>
      <span {...getWrapperProps()}>
        <span {...getControlProps()} />
      </span>
      <VisuallyHidden>
        <input {...getInputProps()} />
      </VisuallyHidden>
    </Component>
  );
};
